/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext, useEffect } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import { CardId } from '../../../../models/diviCard';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { useFreshCallback } from '../../../../utils/hooks';
import { NodeType } from '../../../../backendModels/report.model';
import {
  CONTROL_CENTER_RECORD_ELEMENT_KEY,
  ControlCenterPropertyKeys,
  ControlCenterRecord,
} from '../../../../models/genericElements/controlCenter';
import { Draft } from 'immer';
import { InputState } from '../../../../backendModels/general.model';
import { isGenericRecordDeletable } from '../../../../models/generic';
import {
  changePropertyAndMarkTouched,
  getTouchedClassName,
  handleUntouchedTextFieldUpdates,
} from '../../../../utils/recordWithTouched';
import {
  PATIENT_DATA_RECORD_ELEMENT_KEY,
  PatientDataRecord,
  PatientPropertyKeys,
} from '../../../../models/genericElements/patientData';
import _ from 'lodash';
import DebouncedTextField from '../../../customs/debouncedTextField/DebouncedTextField';
import { nonEmptyString } from '../../../../utils/util';
import { NormalizedMission } from '../../../../models/operational';
import { TextField } from '@mui/material';

export interface MissionProps {
  readonly nodeType: NodeType;
}

export default function Mission({ nodeType }: MissionProps) {
  const {
    findRecordOrDefault,
    findNodes,
    adaptRecord,
    patient,
    patientUpdate,
    missionData,
    missionDataUpdate,
    setLastSynchronizedMissionData,
  } = useContext(ReportsAPIContext);

  const patientRecord = findRecordOrDefault('generic', nodeType, PATIENT_DATA_RECORD_ELEMENT_KEY) as PatientDataRecord;
  const record = findRecordOrDefault('generic', nodeType, CONTROL_CENTER_RECORD_ELEMENT_KEY) as ControlCenterRecord;

  useEffect(() => {
    function saveMissionData(onSuccess: (normalizedMission: NormalizedMission) => void) {
      if (missionData?.controlCenter != null && findNodes(nodeType).length > 0) {
        adaptRecord(
          'generic',
          nodeType,
          (draft: Draft<ControlCenterRecord>, deleteRecord) => {
            draft.inputState = InputState.ENTERED;

            const synchronizedDataUpdate: NormalizedMission = {};
            handleUntouchedTextFieldUpdates(
              draft,
              ControlCenterPropertyKeys.controlCenter,
              missionData.controlCenter?.controlCenter,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(
              draft,
              ControlCenterPropertyKeys.controlCenterLicensePlate,
              missionData.controlCenter?.controlCenterLicensePlate
            );
            handleUntouchedTextFieldUpdates(
              draft,
              ControlCenterPropertyKeys.location,
              missionData.controlCenter?.location,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(
              draft,
              ControlCenterPropertyKeys.personnelNumber,
              missionData.controlCenter?.personnelNumber,
              synchronizedDataUpdate
            );

            if (isGenericRecordDeletable(draft)) {
              deleteRecord();
            }

            onSuccess(synchronizedDataUpdate);
          },
          CONTROL_CENTER_RECORD_ELEMENT_KEY
        );
      }
    }

    function updateLastSynchronizedMission(synchronizedDataUpdate: NormalizedMission) {
      setLastSynchronizedMissionData((oldData) => {
        return { ...oldData, mission: { ...oldData?.mission, ...synchronizedDataUpdate } };
      });
    }

    saveMissionData(updateLastSynchronizedMission);
  }, [missionData, adaptRecord, findNodes, nodeType, setLastSynchronizedMissionData]);

  const handleChangedControlCenterProperty = useFreshCallback((property, value) => {
    changePropertyAndMarkTouched(adaptRecord, nodeType, CONTROL_CENTER_RECORD_ELEMENT_KEY, property, value);
  });

  const handlePatientIdChange = useFreshCallback((value: string) => {
    changePropertyAndMarkTouched(
      adaptRecord,
      NodeType.METADATA,
      PATIENT_DATA_RECORD_ELEMENT_KEY,
      PatientPropertyKeys.patientId,
      nonEmptyString(value) ? { fieldType: 'text', text: value } : undefined
    );
  });

  const handleSaveControlCenterProperty = (propertyKey: ControlCenterPropertyKeys, valueToSave?: string) => {
    if (nonEmptyString(valueToSave)) {
      missionDataUpdate?.({
        ...missionData,
        controlCenter: {
          ...missionData?.controlCenter,
          [propertyKey]: valueToSave,
        },
      });
    } else {
      missionDataUpdate?.({ ...missionData, controlCenter: _.omit(missionData?.controlCenter, [propertyKey]) });
    }
  };

  const handleSavePatientId = (valueToSave: string | undefined) => {
    if (nonEmptyString(valueToSave)) {
      patientUpdate?.({
        ...patient,
        patientId: valueToSave,
      });
    } else {
      patientUpdate?.(_.omit(patient, ['patientId']));
    }
  };

  return (
    <DiviFieldCard cardType={CardId.Mission}>
      <div className='row'>
        <DebouncedTextField
          className={getTouchedClassName(patientRecord, PatientPropertyKeys.patientId)}
          label='Patienten Nr.'
          value={patientRecord.values?.patientId?.text ?? ''}
          onDebounceChange={(value) => handlePatientIdChange(value)}
          onUnfocus={handleSavePatientId}
        />
        <DebouncedTextField
          className={getTouchedClassName(record, ControlCenterPropertyKeys.location)}
          label='Standort'
          value={record.values?.location?.text ?? ''}
          onDebounceChange={(value) =>
            handleChangedControlCenterProperty(
              ControlCenterPropertyKeys.location,
              nonEmptyString(value) ? { text: value, fieldType: 'text' } : undefined
            )
          }
          onUnfocus={(value) => handleSaveControlCenterProperty(ControlCenterPropertyKeys.location, value)}
        />
        <DebouncedTextField
          className={getTouchedClassName(record, ControlCenterPropertyKeys.personnelNumber)}
          label='Pers. Nr.'
          value={record.values?.personnelNumber?.text ?? ''}
          onDebounceChange={(value) =>
            handleChangedControlCenterProperty(
              ControlCenterPropertyKeys.personnelNumber,
              value ? { text: value, fieldType: 'text' } : undefined
            )
          }
          onUnfocus={(value) => handleSaveControlCenterProperty(ControlCenterPropertyKeys.personnelNumber, value)}
        />
        <TextField disabled={true} label='Leitstelle' value={record.values?.controlCenter?.text ?? ''}></TextField>
        <TextField
          disabled={true}
          label='Leitstelle (KFZ-Kennz.)'
          value={record.values?.controlCenterLicensePlate?.text ?? ''}
        />
      </div>
    </DiviFieldCard>
  );
}
